// @spec AST-STR-057
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Shield } from 'lucide-react';
import { useState } from 'react';
import { Toaster } from 'sonner';

import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AuditorControlDetail } from '@/features/auditor-access/components/AuditorControlDetail';
import { AuditorControlList } from '@/features/auditor-access/components/AuditorControlList';
import { AuditorModeBanner } from '@/features/auditor-access/components/AuditorModeBanner';
import { useAuditorStore } from '@/stores/auditor-store';

export const Route = createFileRoute('/auditor/access/$accessId/controls')({
  component: AuditorControlsPage,
});

/**
 * Auditor controls page.
 *
 * Displays a read-only list of controls for an organization
 * that the auditor has access to review.
 */
function AuditorControlsPage() {
  const { accessId } = Route.useParams();
  const navigate = useNavigate();

  const isAuditorMode = useAuditorStore.use.isAuditorMode();
  const currentAccess = useAuditorStore.use.currentAccess();

  const { t } = useTranslation('auditor-access');
  const [selectedControlId, setSelectedControlId] = useState<string | null>(null);

  // Verify user is in auditor mode and has the correct access ID
  if (!isAuditorMode || !currentAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessRequired')}</AlertTitle>
            <AlertDescription>
              {t('routes.accessRequiredDescription')}
            </AlertDescription>
          </Alert>
          <Button variant="outline" className="mt-4" onClick={() => void navigate({ to: '/' })}>
            {t('routes.goToHome')}
          </Button>
        </div>
      </div>
    );
  }

  // Verify the access ID matches the current auditor mode
  if (currentAccess.accessId !== accessId) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.invalidAccess')}</AlertTitle>
            <AlertDescription>
              {t('routes.invalidAccessDescription')}
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() =>
              void navigate({ to: `/auditor/access/${currentAccess.accessId}/controls` })
            }
          >
            {t('routes.goToYourControls')}
          </Button>
        </div>
      </div>
    );
  }

  // Verify the access scope includes 'assets'
  if (!currentAccess.accessScope.includes('assets')) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessDenied')}</AlertTitle>
            <AlertDescription>
              {t('routes.accessDeniedControls')}
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => void navigate({ to: '/dashboard' })}
          >
            {t('routes.goToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  const handleControlClick = (controlId: string) => {
    setSelectedControlId(controlId);
  };

  const handleBackToList = () => {
    setSelectedControlId(null);
  };

  const handleAssetClick = (_assetId: string) => {
    void navigate({ to: `/auditor/access/${accessId}/assets` });
    // Note: This navigates to the assets page. To navigate to a specific asset,
    // we'd need deep linking support which could be added later.
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Auditor mode banner */}
      <AuditorModeBanner hasOtherOrganizations={false} />

      <div className="p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          {/* Page header - only shown in list view */}
          {!selectedControlId && (
            <header className="space-y-1">
              <div className="flex items-center gap-2">
                <Shield className="h-6 w-6 text-amber-600" aria-hidden="true" />
                <h1 className="text-2xl font-semibold text-foreground">{t('routes.auditorControlReview')}</h1>
              </div>
              <p className="text-muted-foreground">
                {t('routes.readOnlyAccessToControls', { orgName: currentAccess.name })}
              </p>
            </header>
          )}

          {/* Content */}
          {selectedControlId ? (
            <AuditorControlDetail
              accessId={accessId}
              controlId={selectedControlId}
              onBack={handleBackToList}
              onAssetClick={handleAssetClick}
            />
          ) : (
            <AuditorControlList
              accessId={accessId}
              organizationName={currentAccess.name}
              onControlClick={handleControlClick}
            />
          )}
        </div>
      </div>

      {/* Toast notifications */}
      <Toaster position="top-right" richColors />
    </div>
  );
}
